import { CommonTooltips, CommonDialogs } from "@mcleod/common";
import {
    ClickEvent, Component, ContextMenuLabelProps, CrudDecorator, DataSourceMode, Dialog, Button,
    Image,
    Label, Layout, LookupModelSearchEvent, Panel, Snackbar, TableRow, TableRowDisplayEvent, Textbox,
    Overlay,
    LabelProps, Checkbox
} from "@mcleod/components";
import { Alignment, Api, DisplayType, HorizontalAlignment, Keys, LogManager, ModelRow, Navigation, StringUtil, UrlUtil } from "@mcleod/core";
import { AutogenLayoutInvoiceHistoryList } from "./autogen/AutogenLayoutInvoiceHistoryList";
import { OrderTabView } from "@mcleod/dispatch";
import { InvoiceHistoryChargesQuickInfo } from "./InvoiceHistoryChargesQuickInfo";
import { setBatchCodeTooltip } from "./EdiUtility";
import { InvoiceHistoryToolPanel } from "./InvoiceHistoryToolPanel";
import { ImageName } from "@mcleod/images";
import { DataDisplayEvent } from "@mcleod/components";
import { DataFusionListUtil } from "./DataFusionListUtil";
import { ModelEdiBilling } from "./models/ModelEdiBilling";
import { EdiBilling } from "./EdiBilling";

interface InvoiceHistoryNavigateProps {
    key?: string;
    newTab?: boolean;
    additionalProps?: {};
    styleProps?: {};
}

const log = LogManager.getLogger("lme.datafusion.InvoiceHistoryList");
const layoutURL = "lme/datafusion/ViewEdiBillingDetail";
export class InvoiceHistoryList extends AutogenLayoutInvoiceHistoryList {
    private static readonly ASSET_MOVEMENT_TYPE = "A";
    private static readonly OUTBOUND = "O";
    private static readonly ERROR_ICON = ImageName.circleErrorSolid;
    private static readonly REJECTED = "R";
    private static readonly INBOUND = "I";

    public static navigateTo(props: InvoiceHistoryNavigateProps): void {
        Navigation.navigateTo(
            layoutURL + `?mode=${props.key ? DataSourceMode.UPDATE : DataSourceMode.ADD}`
            + (props.key ? `&key=${props.key}` : ""),
            { newTab: props.newTab, ...props.styleProps },
            { additionalProps: props.additionalProps }
        );
    }

    override async onLoad() {
        this.textboxReferenceNumbers.addKeyUpListener(event => {
            if (event.domEvent != null && event.key === Keys.ENTER && this.textboxReferenceNumbers.text?.length > 0)
                this.mainDataSource.search(this.mainDataSource.getDataboundValues(DataSourceMode.SEARCH, null, true)).finally(() => {
                    if (!StringUtil.isEmptyString(window.location.search)) // remove param ids from url if needed.
                        Navigation.pseudoNavigateTo(window.location.pathname, null)
                });
        })
        this.layoutInvoiceHistoryToolPanel.addMountListener(() => {
            this.tableInvoices.addMountListener(() => this.layoutInvoiceHistoryToolPanel.initialize(this.tableInvoices));
        })
        const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
        if (urlParams?.outboundErrorSearch) {
            this.switchErrors.checked = true;
            this.textboxDirection.selectedItem = this.textboxDirection.items[1];
            await this.mainDataSource.search({ outbound_error_search: true });
        }
        if (urlParams?.inboundPendingSearch) {
            this.textboxDirection.selectedItem = this.textboxDirection.items[0];
            await this.mainDataSource.search({ inbound_pending_search: true });
        }

        // Add listener to refresh error/warning styling after data loads
        this.mainDataSource.addAfterExecutionListener(() => {
            this.refreshErrorWarningLabels();
        });
    }

    private refreshErrorWarningLabels() {
        // Refresh the styling for all visible rows after data loads
        setTimeout(() => {
            this.tableInvoices.rows.forEach(row => {
                const labelErrors = row.findComponentById("labelErrors") as Label;
                const labelWarnings = row.findComponentById("labelWarnings") as Label;

                if (labelErrors && row.data) {
                    this.setErrorWarningLabelStyling(labelErrors, row.data, true);
                }
                if (labelWarnings && row.data) {
                    this.setErrorWarningLabelStyling(labelWarnings, row.data, false);
                }
            });
        }, 100); // Small delay to ensure all data binding is complete
    }

    switchErrorOnChange() {
        this.textboxErrorDescription.visible = this.switchErrors.checked === true;
        this.textboxErrorType.visible = this.switchErrors.checked === true;
    }

    switchBatchOnChange() {
        this.textboxBeginningBatch.visible = this.switchBatchRange.checked === true;
        this.textboxEndingBatch.visible = this.switchBatchRange.checked === true;
    }

    tableInvoicesOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = event.getTableRow().data as ModelRow;
        tableRow.contextMenuItems = () => InvoiceHistoryToolPanel.getContextMenuItems(this.layoutInvoiceHistoryToolPanel, tableRow);
        const labelOrderId = tableRow.findComponentById("labelOrderId") as Label;
        const labelEdibillingProfilepartnerName = tableRow.findComponentById("labelEdibillingProfilepartnerName") as Label;
        const labelCustomerName = tableRow.findComponentById("labelCustomername") as Label;
        const labelShipmentId = tableRow.findComponentById("labelShipmentId") as Label;
        const labelBOL = tableRow.findComponentById("labelBOL") as Label;
        const labelTotalCharges = tableRow.findComponentById("labelTotalCharges") as Label;
        const labelOutboundStatusDisplay = tableRow.findComponentById("labelOutboundStatusDisplay") as Label;
        const labelBatch = tableRow.findComponentById("labelBatch") as Label;
        const labelErrors = tableRow.findComponentById("labelErrors") as Label;
        const labelWarnings = tableRow.findComponentById("labelWarnings") as Label;
        const panelStatus = tableRow.findComponentById("panelStatus") as Panel;

        const isAsset = modelRow.get("def_move_type") === InvoiceHistoryList.ASSET_MOVEMENT_TYPE;
        if (modelRow.get("order_id")) labelOrderId.addUnderlineWhenMouseOver();
        this.setTooltipContent(labelEdibillingProfilepartnerName, modelRow);
        this.setTooltipContent(labelCustomerName, modelRow);
        if (labelShipmentId?.text?.length > 8 || labelBOL?.text?.length > 8) {
            this.setTooltipContent(labelShipmentId, modelRow);
            this.setTooltipContent(labelBOL, modelRow);
        }
        this.setTooltipContent(labelTotalCharges, modelRow, isAsset);
        setBatchCodeTooltip(labelBatch, modelRow, "I", Alignment.TOP);
        const directionOutbound = modelRow.get("direction") === "O";
        labelOutboundStatusDisplay.visible = directionOutbound;
        this.setStatusColor(labelOutboundStatusDisplay);
        this.setTooltipContent(labelOutboundStatusDisplay, modelRow);
        const isIntercompany = modelRow.getBoolean("intercompany");
        const isSplitPay = modelRow.getBoolean("is_split_bill");
        const isBalanceDue = modelRow.getBoolean("is_balance_due");
        this.setIcons(tableRow, "imageIntercompany", "panelPartner", isIntercompany);
        this.setIconsBillType(tableRow, "imageIsSplitPay", "imageBalanceDue", "labelInvoiceType", isSplitPay, isBalanceDue);
        this.setTooltipContent(labelErrors, modelRow);
        this.setTooltipContent(labelWarnings, modelRow);
        this.setTooltipContent(panelStatus, modelRow);

        // Directly set error and warning label styling using model row data to avoid timing issues
        this.setErrorWarningLabelStyling(labelErrors, modelRow, true);
        this.setErrorWarningLabelStyling(labelWarnings, modelRow, false);
        this.setupStatusPanel(tableRow, modelRow);
    }

    orderNumberOnClick(event: ClickEvent) {
        if ((event.target as Label).text) {
            OrderTabView.navigateTo({ key: (event.target as Label).text, newTab: true });
        }
    }

    setTooltipContent(component: Component, modelRow: ModelRow, isAsset: boolean = false) {
        if (component.id == null) return;

        switch (component.id) {
            case "labelEdibillingProfilepartnerName":
                CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/EdiProfileBillingQuickInfo", null,
                    {
                        "direction": modelRow.get("direction"),
                        "partner_id": modelRow.get("partner_id"),
                        "alt_partner_id": modelRow.get("alt_partner_id"),
                        "version": modelRow.get("version"),
                        "batch": modelRow.get("batch")
                    }
                );
                component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
                return;
            case "labelErrors":
                DataFusionListUtil.setStatusErrorTooltipDynamicContent(component as Label, true);
                break;
            case "labelWarnings":
                DataFusionListUtil.setStatusErrorTooltipDynamicContent(component as Label, false);
                break;
            case "labelCustomername":
                CommonTooltips.setTooltipContentLayout(component, modelRow.get("customer_id"), "lme/ar/CustomerQuickInfo", null);
                component.tooltipDynamicOptions = { position: Alignment.BOTTOM };
                return;
            case "labelShipmentId":
            case "labelBOL":
                CommonTooltips.setTooltipContentTextboxes(component, () => [
                    { caption: "Bill of Lading", displayType: DisplayType.STRING, text: modelRow.get("edi_billing_blnum") },
                    { caption: "Shipment ID", displayType: DisplayType.STRING, text: modelRow.get("edi_billing_shipment_id") }
                ]);
                component.tooltipDynamicOptions = { position: Alignment.LEFT };
                return;
            case "labelTotalCharges":
                component.tooltipDynamicContent = async () => {
                    const layout = await Layout.getLoadedLayout("lme/datafusion/InvoiceHistoryChargesQuickInfo") as InvoiceHistoryChargesQuickInfo;
                    layout.panelCarrier.visible = false;
                    await layout.sourceEdiBilling.search({ id: modelRow.get("id") });
                    await layout.sourceOtherChargeEdi.search({ invoice_id: modelRow.get("id") });
                    if (!isAsset) {
                        await layout.sourceInvoiceMargins.search({ order_id: modelRow.get("order_id") });
                    }
                    return layout;
                };
                break;
            case "labelOutboundStatusDisplay":
                if (modelRow.get("outbound_status_display") !== "Not Sent" && modelRow.get("outbound_status_display") !== "Unknown"
                    && modelRow.get("outbound_status_display") !== "Awaiting API Call") {
                    CommonTooltips.setTooltipContentLayout(component, null, "lme/datafusion/TransmissionStatusQuickInfo", null, {
                        "direction": "O",
                        "func_batch": modelRow.get("batch"),
                        "func_receiver_id": modelRow.get("partner_id"),
                        "receiver_id": modelRow.get("alt_partner_id"),
                        "func_version": modelRow.get("version"),
                        "transaction_type": "I"
                    });
                    component.tooltipDynamicOptions = { position: Alignment.LEFT };
                    return;
                }
                break;
            case "panelStatus":
                if (modelRow.get("direction") === InvoiceHistoryList.INBOUND && modelRow.get("edi_billing_status") === InvoiceHistoryList.REJECTED) {
                    component.tooltipDynamicContent = () => `Rejected Reason: ${modelRow.get("error_text")}`;
                } else {
                    component.tooltip = null;
                }
                return;
        }
    }

    private setErrorWarningLabelStyling(label: Label, modelRow: ModelRow, isError: boolean) {
        if (!label || !modelRow) return;

        const fieldName = isError ? "error_count" : "warning_count";
        const countValue = modelRow.get(fieldName);

        // Set default styling (no errors/warnings)
        label.imageName = ImageName.circleCheckEmpty;
        label.color = "success";

        // Check if there are errors/warnings using the model row data
        const hasIssues = countValue && countValue !== "0 Errors" && countValue !== "0 Warnings";
        if (hasIssues) {
            label.imageName = isError ? ImageName.circleErrorSolid : ImageName.circleError;
            label.color = isError ? "error" : "warning";
        }
    }

    public static addNavigationContextMenuItems(itemProps: Partial<ContextMenuLabelProps>[], ediBillingId: string): Partial<ContextMenuLabelProps>[] {
        if (itemProps != null) {
            const navigationItems = InvoiceHistoryList.createNavigationContextMenuItems(ediBillingId);
            if (itemProps.length > 0 && navigationItems.length > 0) {
                const allVisibleItems = itemProps.filter(item => item.visible);
                const lastVisibleItem = allVisibleItems[allVisibleItems.length - 1];
                if (lastVisibleItem) lastVisibleItem.separatorAfter = true;
                itemProps.push(...navigationItems);
            }
        }
        return itemProps;
    }

    public static createNavigationContextMenuItems(ediBillingId: string): Partial<ContextMenuLabelProps>[] {
        if (ediBillingId == null) return [];
        return [
            {
                text: "Open in New Tab",
                onClick: () => InvoiceHistoryList.navigateTo({
                    key: ediBillingId,
                    newTab: true
                })
            },
            {
                text: "Open in New Window",
                onClick: () => InvoiceHistoryList.navigateTo({
                    key: ediBillingId,
                    newTab: true,
                    styleProps: { windowDecorators: false }
                })
            }
        ]
    }

    private setIcons(tableRow: TableRow, imageId: String, componentId: String, isEnabled: boolean) {
        const image = tableRow.findComponentById(imageId) as Image;
        const component = tableRow.findComponentById(componentId) as Component;

        if (image) {
            image.visible = isEnabled;
        }
        if (component) {
            component.marginLeft = !isEnabled ? 30 : 0;
        }
    }

    private setIconsBillType(tableRow: TableRow, imageId: String, imageBalanceDueId: String, componentId: String, isEnabled: boolean, isEnabledBalanceDue: boolean) {
        const image = tableRow.findComponentById(imageId) as Image;
        const imageBalanceDue = tableRow.findComponentById(imageBalanceDueId) as Image;
        const component = tableRow.findComponentById(componentId) as Component;

        if (image) {
            image.visible = isEnabled;
        }
        if (imageBalanceDue) {
            imageBalanceDue.visible = isEnabledBalanceDue;
        }
        if (component) {
            component.marginLeft = !isEnabled && !isEnabledBalanceDue ? 30 : 0;
        }
    }

    /** This is an event handler for the onClick event of buttonAddBalanceDue. */
    async buttonAddBalanceDueOnClick(event: ClickEvent) {
        try {
            // Define the textbox separately
            const orderTextbox = new Textbox({
                lookupModel: "lme/datafusion/orders-lookup-add-balancedue",
                lookupModelDisplayField: "orders.id",
                lookupModelResultField: "orders.id",
                lookupModelLayout: "lme/datafusion/OrderLookupAddBalanceDue",
                placeholder: "Search for billed orders...",
                captionVisible: false,
                fillRow: true,
                required: true
            });
            //TODO see StatusAddOrderDialog -- need to get partner ID stuff and pass to new endpoint
            orderTextbox.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                //TODO - Add any specific search filters if needed
            });

            // Define the split bill checkbox
            const checkboxSplitbill = new Checkbox({
                caption: "Split Invoice",
                marginTop: 10
            });

            // Define the customer ID textbox (only visible when split bill is checked)
            const textboxCustomerId = new Textbox({
                lookupModel: "lme/datafusion/other-charge-edi-customer-lookup",
                lookupModelDisplayField: "customer_id",
                lookupModelResultField: "customer_id",
                lookupModelLayout: "lme/datafusion/OtherChargeEdiCustomerLookup",
                placeholder: "Select customer...",
                captionVisible: false,
                fillRow: true,
                visible: false,
                marginTop: 10
            });

            // Add listener to show/hide customer textbox based on split bill checkbox
            checkboxSplitbill.addChangeListener(() => {
                textboxCustomerId.visible = checkboxSplitbill.checked;
            });

            // Add listener to drive customer lookup based on selected order
            textboxCustomerId.addBeforeLookupModelSearchListener((event: LookupModelSearchEvent) => {
                const selectedOrderData = orderTextbox.getFirstLookupModelData();
                if (selectedOrderData && selectedOrderData.get("orders.id")) {
                    event.filter.order_id = selectedOrderData.get("orders.id");
                } else if (orderTextbox.text) {
                    // Fallback to text if lookup data not available
                    event.filter.order_id = orderTextbox.text;
                }
            });

            // Create a custom dialog following StatusAddOrderDialog pattern
            const dialog = new Dialog({
                title: "Create Balance Due Transaction",
                width: 650,
                height: 400,
                okVisible: false
            });

            const panelContent = new Panel({
                fillRow: true,
                padding: 16,
                paddingBottom: 24,
                components: [
                    new Label({
                        text: "Select an order to create a Balance Due transaction:",
                        marginBottom: 10
                    }),
                    orderTextbox,
                    checkboxSplitbill,
                    textboxCustomerId
                ]
            });

            const panelButtons = new Panel({
                fillRow: true,
                align: HorizontalAlignment.CENTER,
                marginTop: 16
            });

            const buttonConfirm = new Button({
                caption: "Confirm",
                rowBreak: false,
                default: true,
                marginRight: 10,
                backgroundColor: "primary",
                color: "primary.reverse"
            });

            const buttonCancel = new Button({
                caption: "Cancel",
                rowBreak: false,
                marginRight: 48
            });

            let selectedOrderId = null;

            buttonCancel.addClickListener(() => dialog.close(false));

            buttonConfirm.addClickListener(() => {
                if (!StringUtil.isEmptyString(orderTextbox.text)) {
                    selectedOrderId = orderTextbox.text;
                    dialog.close(true);
                } else {
                    orderTextbox.showAdhocTooltip("Please select an order before continuing.");
                }
            });

            panelButtons.add(buttonCancel);
            panelButtons.add(buttonConfirm);

            dialog.add(panelContent);
            dialog.add(panelButtons);

            dialog.show().then(confirmed => {
                if (confirmed && selectedOrderId) {
                    // Get the selected lookup model data to extract partner information
                    const selectedLookupData = orderTextbox.getFirstLookupModelData();

                    // Prepare API parameters with partner information
                    const apiParams: any = {
                        order_id: selectedOrderId,
                        is_split_bill: checkboxSplitbill.checked,
                        customer_id: textboxCustomerId.text
                    };

                    // Add partner information if available from the lookup data
                    if (selectedLookupData) {
                        if (selectedLookupData.get("edi_customer.partner_id")) {
                            apiParams.partner_id = selectedLookupData.get("edi_customer.partner_id");
                        }
                        if (selectedLookupData.get("edi_customer.version")) {
                            apiParams.version = selectedLookupData.get("edi_customer.version");
                        }
                        if (selectedLookupData.get("edi_customer.alt_partner_id")) {
                            apiParams.alt_partner_id = selectedLookupData.get("edi_customer.alt_partner_id");
                        }
                    }

                    // Call endpoint to add EDI balance due
                    Api.search("lme/datafusion/add-edi-balancedue", apiParams).then(async apiResponse => {
                        if (apiResponse?.data && apiResponse.data.length > 0) {
                            const ediLayout = Layout.getLayout("lme/datafusion/EdiBilling") as EdiBilling;
                            ediLayout.addLayoutLoadListener(() => {
                                ediLayout.textboxInvoiceNoString.enabled = true;
                                ediLayout.textboxShipDate.required = true;
                                ediLayout.textboxDeliveryDate.required = true;
                            });
                            const urlParam = { mode: "add", key: apiResponse.data[0].id };
                            const url = "/lme/datafusion/EdiBilling" + UrlUtil.buildQueryString(urlParam);
                            const ediPanel = new CrudDecorator({
                                layout: ediLayout,
                                mode: DataSourceMode.ADD,
                                pseudoNavigateUrl: url,
                                headerProps: {
                                    showClose: true,
                                    showSaveAndClose: true,
                                },
                                key: apiResponse.data[0].id,
                                providedApiData: apiResponse.data[0],
                            }) as any;

                            await ediPanel.slideIn();

                            // Check for warning and display snackbar after slide-in to ensure it appears on top
                            if (apiResponse.data[0].warning) {
                                const warningPanel = new Panel();
                                const warningLabel = new Label({ text: apiResponse.data[0].warning });
                                warningPanel.add(warningLabel);

                                Snackbar.showWarningSnackbar(warningPanel, {
                                    millisUntilDismissal: 5000, // Auto-dismiss after 5 seconds
                                    targetPanel: ediPanel // Show snackbar on the EDI panel to ensure proper z-index
                                });
                            }
                        } else {
                            Snackbar.showWarningSnackbar("Failed to create Balance Due transaction.");
                        }
                    }).catch(error => {
                        Snackbar.showWarningSnackbar("Error creating Balance Due transaction: " + error.message);
                    });
                }
            }).catch(error => {
                CommonDialogs.showError(error, "Error Creating Balance Due");
            });
        } catch (error) {
            CommonDialogs.showError(error, "Error Creating Balance Due");
        }
    }

    /** This is an event handler for the onDataDisplay event of labelHasErrors. */
    labelHasErrorsOnDataDisplay(event: DataDisplayEvent) {
        DataFusionListUtil.setErrorsLabelColor(event, true);
    }

    /** This is an event handler for the onDataDisplay event of labelHasWarnings. */
    labelHasWarningsOnDataDisplay(event: DataDisplayEvent) {
        DataFusionListUtil.setErrorsLabelColor(event, false);
    }

    // Consolidated status mapping configuration
    private static readonly STATUS_CONFIG = {
        // Database status codes to display names
        DB_TO_DISPLAY: {
            "P": "Pending",
            "A": "Accepted",
            "R": "Rejected",
            "C": "Compared",
            "T": "Already transferred to settlement",
            "D": "Disregarded"
        } as const,

        // Display names to database codes
        DISPLAY_TO_DB: {
            "Pending": "P",
            "Accepted": "A",
            "Rejected": "R",
            "Compared": "C",
            "Already transferred to settlement": "T",
            "Disregarded": "D"
        } as const,

        // Database codes to colors
        DB_TO_COLOR: {
            "P": "warning",
            "A": "success",
            "R": "error",
            "C": "primary",
            "T": "caution"
        } as const,

        // Display names to colors (for legacy support)
        DISPLAY_TO_COLOR: {
            "Pending": "warning",
            "Accepted": "success",
            "Rejected": "error",
            "Compared": "primary",
            "Already transferred to settlement": "caution",
            "Successful": "success",
            "Recovered": "success",
            "Failed": "error"
        } as const,

        // Generate reverse colors dynamically
        get DISPLAY_TO_REVERSE_COLOR() {
            return Object.fromEntries(
                Object.entries(this.DISPLAY_TO_COLOR).map(([key, value]) => [key, `${value}.reverse`])
            );
        }
    };

    private setStatusColor(label: Label): void {
        label.color = InvoiceHistoryList.STATUS_CONFIG.DISPLAY_TO_COLOR[label.text] || "default";
    }

    panelStatusOnClick(event: ClickEvent) {
        const panel = event.target as Panel;
        if (panel["statusDropDownItems"] != null) {
            this.displayStatusDropDowns(panel);
        } else {
            this.populateAndDisplayStatusDropDowns(panel, TableRow.getContainingTableRow(panel).data?.get("id"));
        }
    }

    displayStatusDropDowns(panel: Panel) {
        const dropDownItems = panel["statusDropDownItems"];
        if (dropDownItems != null && dropDownItems.length > 0) {
            Overlay.showDropdown(panel, dropDownItems, null, { width: 225 }, {
                align: Alignment.LEFT,
                onClose: () => {
                    panel.borderShadow = false;
                }
            })
        }
    }

    populateAndDisplayStatusDropDowns(panel: Panel, billingId: string) {
        const dropDownItems: Partial<LabelProps>[] = [];

        // Build dropdown items using consolidated status config
        Object.entries(InvoiceHistoryList.STATUS_CONFIG.DB_TO_DISPLAY).forEach(([statusCode, statusDisplay]) => {
            dropDownItems.push({
                text: statusDisplay,
                onClick: async () => {
                    this.updateBillingRecord(panel, billingId, statusCode);
                }
            });
        });

        panel["statusDropDownItems"] = dropDownItems;
        this.displayStatusDropDowns(panel);
    }

    private async updateBillingRecord(panel: Panel, billingId: string, statusCode: string) {
        const tableRow = TableRow.getContainingTableRow(panel);
        const modelRow = tableRow.data as ModelRow;
        const statusImage = tableRow.findComponentById("imageStatus") as Image;
        const oldStatus = modelRow.get("edi_billing_status");

        if (statusCode === InvoiceHistoryList.REJECTED && oldStatus !== InvoiceHistoryList.REJECTED) {
            const existingReason = modelRow.get("error_text") ?? "";
            const reason = await CommonDialogs.showInputDialog(
                "Enter a reason for the rejection, or modify the existing one:",
                "Reason for Invoice Rejection",
                { text: existingReason }
            );
            if (reason == null) return;
            this.setBusyState(statusImage, true);
            new ModelEdiBilling().searchSingle({ id: billingId })
                .then((billingRow) => {
                    billingRow.set("status", statusCode);
                    billingRow.set("error_text", reason);
                    return billingRow.post();
                })
                .then(row => {
                    log.debug("edi billing invoice posted");
                    this.setColors(panel, statusImage, tableRow.findComponentById("labelBillingStatus") as Label, row.get("status"));
                    panel.tooltip = `Rejected Reason: ${reason}`;
                })
                .catch(reason => {
                    CommonDialogs.showError(reason);
                })
                .finally(() => {
                    this.setBusyState(statusImage, false);
                });
        } else {
            this.setBusyState(statusImage, true);
            new ModelEdiBilling().searchSingle({ id: billingId })
                .then((billingRow) => {
                    billingRow.set("status", statusCode);
                    if (statusCode !== InvoiceHistoryList.REJECTED) {
                        billingRow.set("error_text", null);
                    }
                    return billingRow.post();
                })
                .then(row => {
                    log.debug("edi billing invoice posted");
                    this.setColors(panel, statusImage, tableRow.findComponentById("labelBillingStatus") as Label, row.get("status"));
                    if (statusCode !== InvoiceHistoryList.REJECTED) {
                        panel.tooltip = null;
                    }
                })
                .catch(reason => {
                    CommonDialogs.showError(reason);
                })
                .finally(() => {
                    this.setBusyState(statusImage, false);
                });
        }
    }

    private setBusyState(image: Image | null, busy: boolean) {
        if (image) {
            image.busy = busy;
        }
    }

    setupStatusPanel(tableRow: TableRow, modelRow: ModelRow) {
        const direction = modelRow.get("direction");
        const panelStatus = tableRow.findComponentById("panelStatus") as Panel;

        if (direction === InvoiceHistoryList.OUTBOUND) {
            panelStatus.visible = false;
            return;
        }

        panelStatus.visible = true;
        const labelBillingStatus = tableRow.findComponentById("labelBillingStatus") as Label;
        const imageStatus = tableRow.findComponentById("imageStatus") as Image;
        const status = modelRow.get("edi_billing_status");

        this.setBusyState(imageStatus, false);

        if (panelStatus && status) {
            this.setColors(panelStatus, imageStatus, labelBillingStatus, status);
        }
    }

    private setColors(panel: Panel, image: Image, label: Label, status: string) {
        const statusColor = InvoiceHistoryList.STATUS_CONFIG.DB_TO_COLOR[status] || "default";
        const statusDisplay = InvoiceHistoryList.STATUS_CONFIG.DB_TO_DISPLAY[status] || status || "--";
        const statusColorReverse = InvoiceHistoryList.STATUS_CONFIG.DISPLAY_TO_REVERSE_COLOR[statusDisplay] || "default.reverse";

        panel.borderWidth = 0;
        panel.backgroundColor = statusColor;

        if (label) {
            label.color = statusColorReverse;
            label.text = statusDisplay;
        }
        if (image) {
            image.color = statusColorReverse;
            image.name = ImageName.chevron;
            image.visible = true;
        }
    }
}
